import type {
  AnalysisRow,
  AnalysisStepRow,
  Json,
  TenantCollectionRow,
} from "@/types/supabase.types";
import { MatrixCompatibilityError } from "./types";
import type {
  MatrixCellUi,
  MatrixColumnDef,
  MatrixColumnType,
  MatrixListItemUi,
  MatrixPreviewRowUi,
  MatrixRowUi,
  MatrixUi,
  MatrixValueSource,
} from "./types";

export type MatrixSettings = {
  version: number;
  question: string;
  columns: MatrixColumnDef[];
  templateId?: string;
  structureSignature?: string;
};

export type MatrixCollectionRow = TenantCollectionRow & {
  tenant_collection_setting: { settings: Json | null } | null;
};

export const MATRIX_PREVIEW_ROWS = 3;

export type MatrixListRow = MatrixCollectionRow & {
  analysis: { count: number }[];
  preview: {
    analysis_id: string;
    analysis_setting: { display_input_results: Json } | null;
    // Every step comes along: the embed cannot filter by a name that varies.
    analysisstep: Pick<AnalysisStepRow, "analysis_step_name" | "value">[];
  }[];
};

type MatrixAnalysisStep = Pick<
  AnalysisStepRow,
  | "analysis_step_id"
  | "analysis_step_name"
  | "analysis_step_status"
  | "value"
  | "analysis_order"
> & {
  analysisstep_setting: { settings: Json | null } | null;
};

export type MatrixAnalysisRow = AnalysisRow & {
  analysis_setting: {
    display_name: string;
    display_input_results: Json;
  };
  analysisstep: MatrixAnalysisStep[];
};

export type MatrixDetailRaw = {
  collection: MatrixCollectionRow;
  analyses: MatrixAnalysisRow[];
  signatures?: {
    analysis_id: string;
    template_id: string;
    structure_signature: string;
  }[];
};

const DEFAULT_COLUMN_WIDTH = 180;
const DEFAULT_COLUMN_TYPE: MatrixColumnType = "text";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseColumnType(value: unknown): MatrixColumnType {
  return value === "badge" || value === "list" ? value : DEFAULT_COLUMN_TYPE;
}

function parseColumnDef(raw: unknown): MatrixColumnDef | null {
  if (!isRecord(raw)) return null;

  const key = typeof raw.key === "string" ? raw.key.trim() : "";
  if (!key) return null;

  const label = typeof raw.label === "string" ? raw.label : key;
  const width =
    typeof raw.width === "number" && Number.isFinite(raw.width)
      ? raw.width
      : DEFAULT_COLUMN_WIDTH;
  const order =
    typeof raw.order === "number" && Number.isFinite(raw.order) ? raw.order : 0;
  const displayDescription =
    typeof raw.display_description === "string" &&
    raw.display_description.trim().length > 0
      ? raw.display_description
      : undefined;

  return {
    key,
    label,
    type: parseColumnType(raw.type),
    width,
    order,
    ...(displayDescription ? { displayDescription } : {}),
  };
}

// Null for a missing or malformed shape: a collection is a matrix only when this parses.
export function parseMatrixSettings(
  settings: Json | null | undefined,
): MatrixSettings | null {
  if (!isRecord(settings)) return null;

  const matrix = settings.matrix;
  if (!isRecord(matrix)) return null;

  const rawColumns = Array.isArray(matrix.columns) ? matrix.columns : [];
  const columns = rawColumns
    .map(parseColumnDef)
    .filter((column): column is MatrixColumnDef => column !== null)
    .sort((a, b) => a.order - b.order);

  return {
    version: typeof matrix.version === "number" ? matrix.version : 1,
    question: typeof matrix.question === "string" ? matrix.question : "",
    columns,
    ...(typeof matrix.template_id === "string"
      ? { templateId: matrix.template_id }
      : {}),
    ...(typeof matrix.structure_signature === "string"
      ? { structureSignature: matrix.structure_signature }
      : {}),
  };
}

export function isColumnPrompted(column: MatrixColumnDef): boolean {
  return (
    typeof column.displayDescription === "string" &&
    column.displayDescription.trim().length > 0
  );
}

export function toMatrixListItem(row: MatrixListRow): MatrixListItemUi | null {
  const settings = parseMatrixSettings(
    row.tenant_collection_setting?.settings ?? null,
  );
  if (!settings) return null;

  return {
    collectionId: row.tenant_collection_id,
    name: row.tenant_collection_name,
    rowCount: row.analysis[0]?.count ?? 0,
    updatedAt: row.record_updated,
    columns: settings.columns,
    previewRows: toPreviewRows(row.preview ?? [], settings.columns),
  };
}

// The first column's value per row; an empty one is that column's state, not a gap.
function toPreviewRows(
  preview: MatrixListRow["preview"],
  columns: MatrixColumnDef[],
): MatrixPreviewRowUi[] {
  const [firstColumn] = columns;

  return preview.map((entry) => ({
    analysisId: entry.analysis_id,
    value: firstColumn ? toPreviewValue(entry, firstColumn) : "",
  }));
}

function toPreviewValue(
  entry: MatrixListRow["preview"][number],
  column: MatrixColumnDef,
): string {
  if (isColumnPrompted(column)) {
    const step = (entry.analysisstep ?? []).find(
      (candidate) => candidate.analysis_step_name === column.key,
    );
    return step?.value ?? "";
  }

  return (
    parseInputResults(entry.analysis_setting?.display_input_results)[
      column.key
    ] ?? ""
  );
}

export function matrixItemMatchesSearch(
  item: MatrixListItemUi,
  search: string,
): boolean {
  const trimmed = search.trim().toLowerCase();
  if (!trimmed) return true;
  return item.name.toLowerCase().includes(trimmed);
}

function parseInputResults(
  raw: Json | null | undefined,
): Record<string, string> {
  if (!isRecord(raw)) return {};

  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === "string") result[key] = value;
  }
  return result;
}

function parseMatrixValueSource(
  settings: Json | null | undefined,
): MatrixValueSource | undefined {
  if (!isRecord(settings) || !isRecord(settings.matrix)) return undefined;
  const source = settings.matrix.value_source;
  return source === "manual" || source === "generated" ? source : undefined;
}

function toMatrixRow(
  analysis: MatrixAnalysisRow,
  columns: MatrixColumnDef[],
): MatrixRowUi {
  const displayName = analysis.analysis_setting.display_name.trim();
  if (!displayName) {
    throw new Error(
      `Matrix row ${analysis.analysis_id} is missing analysis_setting.display_name`,
    );
  }

  const inputResults = parseInputResults(
    analysis.analysis_setting.display_input_results,
  );
  const stepsByColumnKey = new Map(
    analysis.analysisstep.map((step) => [step.analysis_step_name, step]),
  );

  const cells: Record<string, MatrixCellUi> = {};
  for (const column of columns) {
    if (isColumnPrompted(column)) {
      const step = stepsByColumnKey.get(column.key);
      const source = parseMatrixValueSource(
        step?.analysisstep_setting?.settings ?? null,
      );
      cells[column.key] = {
        value: step?.value ?? "",
        status: step?.analysis_step_status,
        ...(source ? { source } : {}),
      };
    } else {
      cells[column.key] = { value: inputResults[column.key] ?? "" };
    }
  }

  return {
    analysisId: analysis.analysis_id,
    displayName,
    status: analysis.analysis_status,
    createdAt: analysis.record_created,
    cells,
  };
}

export function toMatrixUi({
  collection,
  analyses,
  signatures = [],
}: MatrixDetailRaw): MatrixUi {
  const settings = parseMatrixSettings(
    collection.tenant_collection_setting?.settings ?? null,
  );
  const columns = settings?.columns ?? [];

  if (settings?.structureSignature) {
    const incompatibleAnalysisIds = signatures
      .filter(
        (signature) =>
          signature.structure_signature !== settings.structureSignature,
      )
      .map((signature) => signature.analysis_id);
    if (incompatibleAnalysisIds.length > 0) {
      throw new MatrixCompatibilityError(
        collection.tenant_collection_id,
        incompatibleAnalysisIds,
      );
    }
  }

  return {
    collectionId: collection.tenant_collection_id,
    name: collection.tenant_collection_name,
    question: settings?.question ?? "",
    columns,
    rows: analyses.map((analysis) => toMatrixRow(analysis, columns)),
  };
}

/**
 * Waiting on the engine. A cell is `idle` until someone starts the matrix, so
 * everything outside these two is a state it will not leave on its own.
 */
export function isMatrixCellActive(cell: MatrixCellUi): boolean {
  return cell.status === "pending" || cell.status === "in progress";
}

export function isMatrixRowActive(row: MatrixRowUi): boolean {
  return row.status === "pending" || row.status === "in progress";
}

export function hasActiveRows(matrix: MatrixUi): boolean {
  return matrix.rows.some(isMatrixRowActive);
}

export function hasActivePromptCells(matrix: MatrixUi): boolean {
  const promptedKeys = new Set(
    matrix.columns.filter(isColumnPrompted).map((column) => column.key),
  );
  return matrix.rows.some((row) =>
    Object.entries(row.cells).some(
      ([key, cell]) => promptedKeys.has(key) && isMatrixCellActive(cell),
    ),
  );
}

export function shouldPollMatrixDetail(matrix: MatrixUi): boolean {
  const hasPromptColumn = matrix.columns.some(isColumnPrompted);
  return hasPromptColumn && hasActiveRows(matrix);
}
